import { SelectOption } from 'components/Forms';
import { PrimaryLanguageEnum, ContactRelationship, InsuranceSubscriberRelationship } from 'domains/client';
import { SIGNUP_CONCERNS_ADULT, SIGNUP_CONCERNS_PEDIATRIC } from 'domains/signupDirect/misc/communicationConcerns';
import { useInsurancesV2 } from 'domains/signupDirect/misc/insuranceOptions';
import { TFunction } from 'i18next';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StateAbbrev } from 'utils/states';

export const therapyTargetValues = ['child', 'adult', 'another-adult'] as const;

export const getTherapyTargetOptions = (t: TFunction) =>
  therapyTargetValues.map(value => ({
    label: t(`directSignup.therapyTarget.${value}`),
    value,
  }));

export type TherapyTargetValue = (typeof therapyTargetValues)[number];

export const SIGNUP_PRIVATE_PAY_OPTION = 'privatePay';
export const INSURANCE_OPTION = 'insurance';
export const OTHER_INSURANCE_OPTION = 'otherInsurance';

export const useStateInsuranceTypeOptions = ({
  stateFilter,
  currentOptionValue,
}: {
  stateFilter?: StateAbbrev;
  currentOptionValue?: string;
} = {}) => {
  const { data, isLoading } = useInsurancesV2();
  const { t } = useTranslation();

  const options: SelectOption[] | undefined = useMemo(() => {
    if (!data || !stateFilter) return undefined;
    const options: SelectOption[] = data
      .filter(o => !stateFilter || o.state === stateFilter)
      .map(({ displayName: name, entryId }) => ({ value: entryId, label: name }));

    return [
      {
        value: SIGNUP_PRIVATE_PAY_OPTION,
        label: t('directSignup.insurance.privatePay'),
      },
      {
        value: OTHER_INSURANCE_OPTION,
        label: t('directSignup.insurance.otherInsurance'),
      },
      ...sortBy(options, 'label'),
    ];
  }, [data, stateFilter]);

  const currentInsuranceData = useMemo(() => {
    return data?.find(insurance => insurance.payerId === currentOptionValue);
  }, [currentOptionValue, data]);

  return { options, isLoading, insuranceData: data, currentInsuranceData };
};

export type InsuranceTypeValue = string;

export const getRelationshipOptions = (t: TFunction) =>
  Object.values(ContactRelationship).map(value => ({
    label: t(`directSignup.relationships.${value}`),
    value,
  }));

export const getSubscriberRelationshipOptions = (t: TFunction) =>
  Object.values(InsuranceSubscriberRelationship).map(value => ({
    label: t(`directSignup.sections.insurance.subscriberRelationships.${value}`),
    value,
  }));

export type RelationshipValue = ContactRelationship;

export const getSpecialtyOptionsPediatric = (t: TFunction, isResumeLead?: boolean) =>
  SIGNUP_CONCERNS_PEDIATRIC
    .filter(value => !isResumeLead || value.key !== 'Other')
    .map(value => ({
      label: t(`directSignup.concerns.${value.key}`),
      value: value.key,
    }));

export const getSpecialtyOptionsAdult = (t: TFunction, isResumeLead?: boolean) =>
  SIGNUP_CONCERNS_ADULT
    .filter(value => !isResumeLead || value.key !== 'Other')
    .map(value => ({
      label: t(`directSignup.concerns.${value.key}`),
      value: value.key,
    }));

export const getLanguageOptions = (): SelectOption[] =>
  Object.entries(PrimaryLanguageEnum).map(([key, value]) => ({
    label: key,
    value,
  }));
